import { Controller } from "@hotwired/stimulus"
import { prefersReducedMotion } from "../lib/motion"
import { NativeBridge } from "../lib/native_bridge"
import { ONBOARDING_DONE_EVENT, ONBOARDING_SEEN_KEY, onboardingSeen } from "../lib/onboarding"

// First-run intro. Shown once per device (localStorage), after the splash. The track follows the finger;
// each 3D layer moves by its own depth so the art parallaxes, a flick or a long drag pages, and every scene
// entrance pops its art in and floats it. GSAP is loaded lazily; reduced motion gets plain cross-fades.
const FLICK_VELOCITY = 0.45 // px/ms
const PAGE_DISTANCE_RATIO = 0.22 // of the viewport width

export default class extends Controller {
  static targets = [ "viewport", "track", "scene", "layer", "copy", "dot", "nextButton", "nextLabel" ]
  static values = { lastIndex: Number, needsLocation: Boolean }

  connect() {
    if (onboardingSeen()) {
      this.element.remove()
      return
    }

    this.index = 0
    // Finishing in another window or tab WebView closes this copy too.
    this.boundStorage = (event) => {
      if (event.key === ONBOARDING_SEEN_KEY && event.newValue === "1") this.finish({ remember: false })
    }
    window.addEventListener("storage", this.boundStorage)
    this.waitForSplash()
  }

  disconnect() {
    clearTimeout(this.splashTimer)
    window.removeEventListener("storage", this.boundStorage)
    // Left mid-intro (a visit, a push tap): give the tab bar back.
    if (this.hidTabBar && !this.done) NativeBridge.TabBar.show()
    this.floatTweens?.forEach((tween) => tween.kill())
    window.removeEventListener("pointermove", this.boundMove)
    window.removeEventListener("pointerup", this.boundUp)
    window.removeEventListener("pointercancel", this.boundUp)
  }

  waitForSplash() {
    if (document.getElementById("splash-screen")) {
      this.splashTimer = setTimeout(() => this.waitForSplash(), 150)
      return
    }
    this.open()
  }

  async open() {
    this.reduced = prefersReducedMotion()
    if (!this.reduced) {
      const { default: gsap } = await import("gsap")
      this.gsap = gsap
    }
    if (this.done || !this.element.isConnected) return

    this.element.classList.remove("hidden")
    NativeBridge.TabBar.hide()
    this.hidTabBar = true
    this.viewportTarget.focus({ preventScroll: true })
    requestAnimationFrame(() => this.element.classList.add("is-open"))
    this.enterScene(0)
  }

  // --- Paging ---

  next() {
    if (this.index >= this.lastIndexValue) {
      this.finish({ openLocation: true })
      return
    }
    this.goTo(this.index + 1)
  }

  previous() {
    if (this.index > 0) this.goTo(this.index - 1)
  }

  goTo(index, velocity = 0) {
    const from = this.index
    this.index = Math.max(0, Math.min(this.lastIndexValue, index))
    this.sceneTargets.forEach((scene, i) => scene.setAttribute("aria-hidden", i === this.index ? "false" : "true"))
    this.dotTargets.forEach((dot, i) => dot.classList.toggle("is-active", i === this.index))
    const label = this.nextLabelTarget
    label.textContent = this.index === this.lastIndexValue ? label.dataset.startLabel : label.dataset.nextLabel
    this.nextButtonTarget.classList.toggle("is-start", this.index === this.lastIndexValue)

    const x = -this.index * this.viewportTarget.clientWidth
    if (this.gsap) {
      // Faster flicks settle faster; the landing always eases out.
      const duration = Math.max(0.32, 0.6 - Math.min(Math.abs(velocity), 2) * 0.12)
      this.gsap.to(this.trackTarget, { x, duration, ease: "expo.out" })
      this.gsap.to(this.layerTargets, { x: 0, duration, ease: "expo.out" })
    } else {
      this.trackTarget.style.transform = `translateX(${x}px)`
    }
    if (from !== this.index) this.enterScene(this.index)
  }

  // Scene entrance: the art pops in by depth (nearest first), text rises, then the art floats gently.
  enterScene(index) {
    const scene = this.sceneTargets[index]
    const layers = [ ...scene.querySelectorAll("[data-onboarding-intro-target='layer']") ]
      .sort((a, b) => Number(b.dataset.depth) - Number(a.dataset.depth))
    const copy = scene.querySelectorAll("[data-onboarding-intro-target='copy']")

    if (!this.gsap) {
      scene.classList.add("is-entered")
      return
    }

    this.floatTweens?.forEach((tween) => tween.kill())
    const gsap = this.gsap
    const art = layers.map((layer) => layer.querySelector(".onboarding-art"))
    const entrance = (kind) => art.filter((el) => (el.parentElement.dataset.enter || "") === kind)
    gsap.fromTo(entrance(""),
      { opacity: 0, scale: 0.72, y: 28, rotate: (i) => (i % 2 ? 7 : -7) },
      { opacity: 1, scale: 1, y: 0, rotate: 0, duration: 0.7, ease: "back.out(1.6)", stagger: 0.09, overwrite: true })
    // An ad card rises out of the phone's feed, then its label pops in.
    gsap.fromTo(entrance("lift"),
      { opacity: 0, x: -70, y: 34, scale: 0.55, rotate: -10 },
      { opacity: 1, x: 0, y: 0, scale: 1, rotate: 0, duration: 0.8, ease: "back.out(1.4)", delay: 0.3, overwrite: true })
    gsap.fromTo(entrance("chip"),
      { opacity: 0, scale: 0.4, y: 10 },
      { opacity: 1, scale: 1, y: 0, duration: 0.5, ease: "back.out(2.2)", delay: 0.8, overwrite: true })
    gsap.fromTo(copy,
      { opacity: 0, y: 14 },
      { opacity: 1, y: 0, duration: 0.45, ease: "power3.out", stagger: 0.07, delay: 0.18, overwrite: true })
    const floatDelay = { lift: 1.15, chip: 1.35 }
    this.floatTweens = art.map((el, i) => gsap.to(el, {
      y: i % 2 ? 7 : -8, duration: 2.2 + i * 0.35, ease: "sine.inOut", yoyo: true, repeat: -1,
      delay: floatDelay[el.parentElement.dataset.enter] ?? 0.75
    }))
  }

  // --- Drag / swipe ---

  dragStart(event) {
    if (event.button !== undefined && event.button !== 0) return
    this.dragging = true
    this.startX = event.clientX
    this.lastX = event.clientX
    this.lastT = event.timeStamp
    this.velocity = 0
    this.width = this.viewportTarget.clientWidth
    this.baseX = -this.index * this.width
    this.gsap?.killTweensOf([ this.trackTarget, ...this.layerTargets ])
    this.boundMove = (e) => this.dragMove(e)
    this.boundUp = (e) => this.dragEnd(e)
    window.addEventListener("pointermove", this.boundMove)
    window.addEventListener("pointerup", this.boundUp, { once: true })
    // A cancelled gesture (the OS took the touch) still settles on the nearest scene instead of freezing mid-drag.
    window.addEventListener("pointercancel", this.boundUp, { once: true })
  }

  dragMove(event) {
    if (!this.dragging) return
    let dx = event.clientX - this.startX
    const atEdge = (this.index === 0 && dx > 0) || (this.index === this.lastIndexValue && dx < 0)
    if (atEdge) dx *= 0.3 // rubber band past the first/last scene
    const dt = Math.max(1, event.timeStamp - this.lastT)
    this.velocity = (event.clientX - this.lastX) / dt
    this.lastX = event.clientX
    this.lastT = event.timeStamp

    this.trackTarget.style.transform = `translateX(${this.baseX + dx}px)`
    // Parallax: near layers travel further than far ones.
    this.layerTargets.forEach((layer) => {
      layer.style.transform = `translateX(${dx * (Number(layer.dataset.depth) - 0.5) * 0.5}px)`
    })
  }

  dragEnd(event) {
    if (!this.dragging) return
    this.dragging = false
    window.removeEventListener("pointermove", this.boundMove)
    window.removeEventListener("pointerup", this.boundUp)
    window.removeEventListener("pointercancel", this.boundUp)
    // pointercancel reports no usable position; fall back to the last move.
    const endX = event.type === "pointercancel" || !event.clientX ? this.lastX : event.clientX
    const dx = endX - this.startX
    const flick = Math.abs(this.velocity) > FLICK_VELOCITY
    const far = Math.abs(dx) > this.width * PAGE_DISTANCE_RATIO
    let target = this.index
    if (flick || far) target += (flick ? this.velocity : dx) < 0 ? 1 : -1
    if (this.gsap) this.gsap.set(this.trackTarget, { x: this.baseX + dx })
    this.trackTarget.style.transform = this.gsap ? "" : this.trackTarget.style.transform
    if (!this.gsap) this.layerTargets.forEach((layer) => { layer.style.transform = "" })
    this.goTo(target, this.velocity)
  }

  // --- Done ---

  finish({ remember = true, openLocation = false } = {}) {
    if (this.done) return
    this.done = true
    clearTimeout(this.splashTimer)
    if (remember) {
      try { localStorage.setItem(ONBOARDING_SEEN_KEY, "1") } catch { /* private mode: show again next time */ }
    }
    if (this.hidTabBar) NativeBridge.TabBar.show()
    const needsLocation = this.needsLocationValue
    this.element.classList.remove("is-open")
    this.element.classList.add("is-leaving")
    const remove = () => {
      this.element.remove()
      window.dispatchEvent(new CustomEvent(ONBOARDING_DONE_EVENT))
      // 開始探索 on the location scene opens location setup for a signed-in user who has none.
      if (openLocation && needsLocation) window.dispatchEvent(new CustomEvent("gps:open", { detail: { autoShow: true } }))
    }
    if (this.reduced || !this.hidTabBar) remove()
    else setTimeout(remove, 360)
  }
}
